import React, { Component } from 'react'
import { Tag, Spin, Icon, Popover, Descriptions, Input, Button } from 'antd'
import moment from 'moment'
import Request, { API_URL } from '../../../request'
import MediaViewer from '../../../components/MediaViewer'

// Staged drag-and-drop image board embedded at the top of the Add/Edit Outward
// form. It does NOT auto-save — the parent form reads getSelection() on submit
// and links the images to the order (link-on-submit). `general` is the pool of
// available (unmapped) outward images; the 5 columns are this order's images.
const COLUMNS = [
  { key: 'manufacturing_order_outward', title: 'Manufacturing', accent: '#1890ff' },
  { key: 'consumable', title: 'Consumable', accent: '#13c2c2' },
  { key: 'dispatch_order', title: 'Dispatch', accent: '#722ed1' },
  { key: 'repair_order', title: 'Repair', accent: '#fa8c16' },
  { key: 'outward_for_guard', title: 'Outward for Guard', accent: '#52c41a' },
]
const COL_KEYS = COLUMNS.map((c) => c.key)
const ALL_KEYS = ['general'].concat(COL_KEYS)

// the Manufacturing category also shows Production Orders as drop-to-map boxes
const MANUFACTURING_KEY = 'manufacturing_order_outward'
// the Dispatch category shows the Order List (Order Dispatch) as drop-to-map boxes
const DISPATCH_KEY = 'dispatch_order'
// the Repair category shows the same Order List, filtered to Repaired Goods
const REPAIR_KEY = 'repair_order'
// exact stored goodsType values for the order list (Finished has TWO spaces)
const FINISHED_GOODS = 'Finished  Goods'
const REPAIRED_GOODS = 'Repaired Goods'
// max order boxes rendered at once (the lists can be ~1000 — rendering them all
// makes the board sluggish/janky while dragging). Search to reach the rest.
const ORDER_BOX_CAP = 50

const emptyColumns = () =>
  ALL_KEYS.reduce((acc, k) => {
    acc[k] = []
    return acc
  }, {})

const fullUrl = (u) => {
  if (!u) return ''
  if (u.indexOf('http') === 0) return u
  return API_URL + (u.charAt(0) === '/' ? '' : '/') + u
}

// newest first — by createdAt, falling back to _id (which embeds a timestamp)
const byLatest = (a, b) => {
  const ta = a && a.createdAt ? new Date(a.createdAt).getTime() : 0
  const tb = b && b.createdAt ? new Date(b.createdAt).getTime() : 0
  if (tb !== ta) return tb - ta
  return String((b && b._id) || '').localeCompare(String((a && a._id) || ''))
}

const cardStyle = {
  position: 'relative',
  width: 82,
  height: 82,
  borderRadius: 6,
  overflow: 'hidden',
  background: '#000',
  border: '1px solid #e8e8e8',
  cursor: 'grab',
  flex: '0 0 auto',
}
const mediaStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }

class OutwardImageBoard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      loaded: false, // true once a fetch has populated the columns
      columns: emptyColumns(),
      dragged: null,
      overCol: null,
      overIndex: null,
      previewItems: [], // list the lightbox navigates through
      previewIndex: -1, // -1 = closed
      remarks: {}, // photoId -> per-image remark typed in a category column
      // per-section search text (order lists can be huge — cap + search)
      poSearch: '',
      dispatchSearch: '',
      repairSearch: '',
      // Production Orders shown as drop-to-map boxes in Manufacturing
      productionOrders: [],
      poLoading: false,
      poMedia: {}, // productionOrderFormId(_id) -> { loading, items } (mapped)
      overPO: null, // production order box currently being dragged over
      // production orders this outward dispatch is linked to (order _ids).
      // Seeded from props on edit; saved back via getSelection() on submit.
      linkedPOs: (props.productionOrderFormIds || []).map(String),
      // Dispatch category: Order List (Order Dispatch, goodsType Finished Goods)
      dispatchOrders: [],
      dispatchLoading: false,
      dispatchMedia: {}, // orderDispatch _id -> { items } (this outward's images)
      linkedDispatchOrders: (props.orderDispatchIds || []).map(String),
      // Repair category: same Order List, filtered to goodsType Repaired Goods
      repairOrders: [],
      repairLoading: false,
      repairMedia: {}, // orderDispatch _id -> { items } (this outward's images)
      linkedRepairOrders: (props.repairOrderIds || []).map(String),
      // click-to-add: an order marked as the active target — clicking Available
      // images then adds them to that order's box (alternative to drag & drop)
      addTarget: null, // { kind: 'production'|'dispatch'|'repair', id, label }
    }
    // production orders whose own uploaded images have already been fetched into
    // their box (so we don't refetch/duplicate on every re-link)
    this.poMediaFetched = new Set()
    // same, for Dispatch/Repair (Order Dispatch) orders' own uploaded images
    this.orderDispatchMediaFetched = new Set()
  }

  // the parent form only writes photos when the board is loaded, so a submit
  // before/while loading can never accidentally clobber the order's images
  isReady = () => this.state.loaded

  componentDidMount() {
    this.load()
    this.loadProductionOrders()
    this.loadDispatchOrders()
    this.loadRepairOrders()
    // auto-fill boxes for orders already linked to this outward (edit)
    this.ensurePOMedia(this.state.linkedPOs)
    this.ensureOrderDispatchMedia(
      this.state.linkedDispatchOrders.concat(this.state.linkedRepairOrders)
    )
  }

  componentDidUpdate(prevProps) {
    // the form sets its record id after an async fetch (edit) — reload then
    if (prevProps.inwardId !== this.props.inwardId) this.load()
    // seed the linked production orders when the form loads them (edit)
    const prev = (prevProps.productionOrderFormIds || []).map(String).join(',')
    const cur = (this.props.productionOrderFormIds || []).map(String).join(',')
    if (prev !== cur) {
      const ids = (this.props.productionOrderFormIds || []).map(String)
      this.setState({ linkedPOs: ids })
      // pull each linked PO's own uploaded images into its box (edit)
      this.ensurePOMedia(ids)
    }
    // seed the linked order-dispatch orders (edit)
    const prevD = (prevProps.orderDispatchIds || []).map(String).join(',')
    const curD = (this.props.orderDispatchIds || []).map(String).join(',')
    if (prevD !== curD) {
      const ids = (this.props.orderDispatchIds || []).map(String)
      this.setState({ linkedDispatchOrders: ids })
      this.ensureOrderDispatchMedia(ids)
    }
    // seed the linked repair orders (edit)
    const prevR = (prevProps.repairOrderIds || []).map(String).join(',')
    const curR = (this.props.repairOrderIds || []).map(String).join(',')
    if (prevR !== curR) {
      const ids = (this.props.repairOrderIds || []).map(String)
      this.setState({ linkedRepairOrders: ids })
      this.ensureOrderDispatchMedia(ids)
    }
  }

  // exposed to the parent form (via ref): images placed in the 5 columns become
  // this order's photos, with their column (subcategory) and position
  getSelection = () => {
    const photos = []
    const photoItems = []
    COLUMNS.forEach((c) => {
      ;(this.state.columns[c.key] || []).forEach((p, i) => {
        photos.push(p._id)
        const rm = this.state.remarks[p._id]
        photoItems.push({
          _id: p._id,
          subCategory: c.key,
          boardPosition: i,
          remarks: rm != null ? rm : p.remarks || '',
        })
      })
    })
    // images in a Production Order box attach to this outward, tagged with their
    // production order (link-on-submit, scoped by outward — so once saved they are
    // "consumed" and won't appear in another Add flow).
    //  - Outward images dropped on the box get the Manufacturing subcategory/position.
    //  - The PO's OWN uploaded images (production-category, auto-filled) keep their
    //    category + lane untouched; we only add the outward link + re-affirm the PO id.
    // Only LINKED orders map their images. Every visible order's images are
    // pre-loaded into *Media state for display (count + preview) without a click,
    // so we must map strictly from the linked lists — not every loaded order.
    this.state.linkedPOs.forEach((poId) => {
      const items = (this.state.poMedia[poId] && this.state.poMedia[poId].items) || []
      items.forEach((p, i) => {
        photos.push(p._id)
        const isProd =
          p.category === 'production_order' || p.category === 'manufacturing_order'
        const item = { _id: p._id, productionOrderFormId: poId }
        if (!isProd) {
          item.subCategory = MANUFACTURING_KEY
          item.boardPosition = i
        }
        photoItems.push(item)
      })
    })
    // images in a linked Dispatch (Order List) box — tagged orderDispatchId
    this.state.linkedDispatchOrders.forEach((odId) => {
      const items = (this.state.dispatchMedia[odId] && this.state.dispatchMedia[odId].items) || []
      items.forEach((p, i) => {
        photos.push(p._id)
        photoItems.push({
          _id: p._id,
          subCategory: DISPATCH_KEY,
          boardPosition: i,
          orderDispatchId: odId,
        })
      })
    })
    // images in a linked Repair box — same order-dispatch source, repair subcat
    this.state.linkedRepairOrders.forEach((odId) => {
      const items = (this.state.repairMedia[odId] && this.state.repairMedia[odId].items) || []
      items.forEach((p, i) => {
        photos.push(p._id)
        photoItems.push({
          _id: p._id,
          subCategory: REPAIR_KEY,
          boardPosition: i,
          orderDispatchId: odId,
        })
      })
    })
    // orders this dispatch is linked to (saved on the outward record)
    return {
      photos,
      photoItems,
      productionOrderFormIds: this.state.linkedPOs.slice(),
      orderDispatchIds: this.state.linkedDispatchOrders.slice(),
      repairOrderIds: this.state.linkedRepairOrders.slice(),
    }
  }

  load = () => {
    this.setState({ loading: true, loaded: false })
    // the outward record's _id (the prop is named inwardId but holds the outward id)
    const outwardId = this.props.inwardId
    Request.outwardFormBoard(outwardId ? { outwardId } : {}).then(({ error, data }) => {
      if (error) {
        this.setState({ loading: false })
        return
      }
      const columns = emptyColumns()
      const poMedia = {}
      const dispatchMedia = {}
      const repairMedia = {}
      ;(data || []).forEach((p) => {
        // this order's images: linked via outwardId (or legacy inwardId)
        const attachedHere =
          !!outwardId &&
          ((p.outwardId && String(p.outwardId) === String(outwardId)) ||
            (p.inwardId && String(p.inwardId) === String(outwardId)))
        // this order's PO-box images route back into their Production Order box
        if (attachedHere && p.productionOrderFormId) {
          const k = String(p.productionOrderFormId)
          if (!poMedia[k]) poMedia[k] = { items: [] }
          poMedia[k].items.push(p)
          return
        }
        // this order's order-dispatch images route back into their box — Repair
        // vs Dispatch is distinguished by the stored subcategory
        if (attachedHere && p.orderDispatchId) {
          const k = String(p.orderDispatchId)
          const bucket = p.subCategory === REPAIR_KEY ? repairMedia : dispatchMedia
          if (!bucket[k]) bucket[k] = { items: [] }
          bucket[k].items.push(p)
          return
        }
        const col =
          attachedHere && COL_KEYS.indexOf(p.subCategory) !== -1 && p.subCategory !== 'general'
            ? p.subCategory
            : 'general'
        columns[col].push(p)
      })
      this.setState({ loading: false, loaded: true, columns, poMedia, dispatchMedia, repairMedia })
    })
  }

  reload = () => {
    // reset per-form selections + order-box images (used after a new outward is
    // created); load() repopulates them for the record being edited
    this.poMediaFetched = new Set()
    this.orderDispatchMediaFetched = new Set()
    this.setState({
      linkedPOs: (this.props.productionOrderFormIds || []).map(String),
      linkedDispatchOrders: (this.props.orderDispatchIds || []).map(String),
      linkedRepairOrders: (this.props.repairOrderIds || []).map(String),
      poMedia: {},
      dispatchMedia: {},
      repairMedia: {},
    })
    this.load()
  }

  // the currently-visible (search-filtered, capped) order ids of a list — the
  // boxes actually rendered. Used to pre-load their image counts on open/scroll.
  visibleIds = (orders, search, matchFn) => {
    const q = (search || '').trim().toLowerCase()
    const filtered = q ? (orders || []).filter((o) => matchFn(o, q)) : orders || []
    return filtered.slice(0, ORDER_BOX_CAP).map((o) => String(o._id))
  }

  // ---- Production Orders (Manufacturing category, drop-to-map boxes) ----
  // pull the same list the Production Order List page uses (no duplicate data)
  loadProductionOrders = () => {
    this.setState({ poLoading: true })
    Request.productionOrderFormList({
      results: 1000,
      page: 1,
      sortField: 'productionOrderId',
      sortOrder: 'desc',
    })
      .then((res) => {
        const rows = (res && res.data) || []
        this.setState({ productionOrders: rows, poLoading: false })
        // pre-load images for the visible boxes so each shows its count on open
        this.ensurePOMedia(this.visibleIds(rows, this.state.poSearch, this.matchProductionOrder))
      })
      .catch(() => this.setState({ poLoading: false }))
  }

  // Fetch a production order's OWN uploaded images (production-category) and merge
  // them into its box, so selecting/linking a PO auto-fills the images uploaded
  // under it. Those images are stamped with this outward on submit (getSelection)
  // while keeping their productionOrderFormId + category (they stay in the PO
  // gallery). Each id is fetched once per session to avoid refetch/duplicates.
  ensurePOMedia = (ids) => {
    const fresh = (ids || [])
      .map(String)
      .filter(Boolean)
      .filter((id) => !this.poMediaFetched.has(id))
    if (!fresh.length) return
    fresh.forEach((id) => this.poMediaFetched.add(id))
    const outwardId = this.props.inwardId
    Request.mappedProductionMedia({ ids: fresh.join(','), outwardId })
      .then((res) => {
        if (!res || res.error) return
        const byForm = {}
        ;(res.data || []).forEach((p) => {
          const k = String(p.productionOrderFormId)
          if (!byForm[k]) byForm[k] = []
          byForm[k].push(p)
        })
        if (!Object.keys(byForm).length) return
        this.setState((s) => {
          const poMedia = { ...s.poMedia }
          Object.keys(byForm).forEach((k) => {
            const existing = (poMedia[k] && poMedia[k].items) || []
            const seen = new Set(existing.map((m) => String(m._id)))
            const add = byForm[k].filter((m) => !seen.has(String(m._id)))
            if (add.length) poMedia[k] = { items: existing.concat(add) }
          })
          return { poMedia }
        })
      })
      .catch(() => {})
  }

  // Fetch a Dispatch/Repair (Order Dispatch) order's OWN uploaded images and merge
  // them into its box. These are outward-category images tagged with orderDispatchId
  // and told apart (Dispatch vs Repair) by their subcategory — routed into the
  // matching box just like load() does. Stamped with this outward on submit while
  // keeping their orderDispatchId. Each id is fetched once per session.
  ensureOrderDispatchMedia = (ids) => {
    const fresh = (ids || [])
      .map(String)
      .filter(Boolean)
      .filter((id) => !this.orderDispatchMediaFetched.has(id))
    if (!fresh.length) return
    fresh.forEach((id) => this.orderDispatchMediaFetched.add(id))
    const outwardId = this.props.inwardId
    Request.mappedOrderDispatchMedia({ ids: fresh.join(','), outwardId })
      .then((res) => {
        if (!res || res.error) return
        const dispatchByOrder = {}
        const repairByOrder = {}
        ;(res.data || []).forEach((p) => {
          const k = String(p.orderDispatchId)
          const bucket = p.subCategory === REPAIR_KEY ? repairByOrder : dispatchByOrder
          if (!bucket[k]) bucket[k] = []
          bucket[k].push(p)
        })
        if (!Object.keys(dispatchByOrder).length && !Object.keys(repairByOrder).length) return
        const mergeInto = (state, byOrder) => {
          const next = { ...state }
          Object.keys(byOrder).forEach((k) => {
            const existing = (next[k] && next[k].items) || []
            const seen = new Set(existing.map((m) => String(m._id)))
            const add = byOrder[k].filter((m) => !seen.has(String(m._id)))
            if (add.length) next[k] = { items: existing.concat(add) }
          })
          return next
        }
        this.setState((s) => ({
          dispatchMedia: mergeInto(s.dispatchMedia, dispatchByOrder),
          repairMedia: mergeInto(s.repairMedia, repairByOrder),
        }))
      })
      .catch(() => {})
  }

  // ---- Order List (Dispatch category, drop-to-map boxes) ----
  // pull the Order Dispatch list, keeping only Finished Goods orders
  loadDispatchOrders = () => {
    this.setState({ dispatchLoading: true })
    Request.orderDispatchList({
      goodsType: FINISHED_GOODS,
      results: 1000,
      page: 1,
      sortField: 'createdAt',
      sortOrder: 'desc',
    })
      .then((res) => {
        const all = (res && res.data) || []
        // guard client-side too — the exact stored value has two spaces — and
        // sort newest first
        const rows = all.filter((o) => o && o.goodsType === FINISHED_GOODS).sort(byLatest)
        this.setState({ dispatchOrders: rows, dispatchLoading: false })
        // pre-load images for the visible boxes so each shows its count on open
        this.ensureOrderDispatchMedia(
          this.visibleIds(rows, this.state.dispatchSearch, this.matchOrderDispatch)
        )
      })
      .catch(() => this.setState({ dispatchLoading: false }))
  }

  // ---- Repair (Order List, goodsType Repaired Goods) ----
  loadRepairOrders = () => {
    this.setState({ repairLoading: true })
    Request.orderDispatchList({
      goodsType: REPAIRED_GOODS,
      results: 1000,
      page: 1,
      sortField: 'createdAt',
      sortOrder: 'desc',
    })
      .then((res) => {
        const all = (res && res.data) || []
        const rows = all.filter((o) => o && o.goodsType === REPAIRED_GOODS).sort(byLatest)
        this.setState({ repairOrders: rows, repairLoading: false })
        // pre-load images for the visible boxes so each shows its count on open
        this.ensureOrderDispatchMedia(
          this.visibleIds(rows, this.state.repairSearch, this.matchOrderDispatch)
        )
      })
      .catch(() => this.setState({ repairLoading: false }))
  }

  // link / unlink a production order to this outward dispatch (saved on submit)
  linkPO = (id) => {
    this.setState((s) =>
      s.linkedPOs.indexOf(id) === -1 ? { linkedPOs: s.linkedPOs.concat(id) } : null
    )
  }

  toggleLinkPO = (order, e) => {
    if (e) e.stopPropagation()
    const id = String(order._id)
    const isLinking = this.state.linkedPOs.indexOf(id) === -1
    // one category at a time — can't link Manufacturing while another is active
    if (isLinking && !this.isDroppable(MANUFACTURING_KEY)) return
    if (isLinking) {
      this.setAddTarget('production', order) // link + make it the click-to-add target
      return
    }
    this.setState((s) => ({
      linkedPOs: s.linkedPOs.filter((k) => k !== id),
      addTarget: s.addTarget && s.addTarget.id === id ? null : s.addTarget,
    }))
  }

  onPOBoxDragOver = (e, order) => {
    if (!this.state.dragged) return
    e.preventDefault()
    e.stopPropagation() // don't let the column's outward drop handler fire
    const key = String(order._id)
    if (this.state.overPO !== key || this.state.overCol != null) {
      this.setState({ overPO: key, overCol: null, overIndex: null })
    }
  }

  // drop an image onto a production order box => map it to that order
  onPOBoxDrop = (e, order) => {
    e.preventDefault()
    e.stopPropagation()
    const { dragged } = this.state
    if (!dragged) {
      this.setState({ overPO: null })
      return
    }
    if (!this.isDroppable(MANUFACTURING_KEY)) {
      // another category is active — one category at a time
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const from = dragged.from
    const srcArr = this.state.columns[from] || []
    const img = srcArr.find((i) => String(i._id) === String(dragged.id))
    if (!img) {
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const key = String(order._id)
    // staged: pull the image out of its source column, add to this order's box.
    // Nothing is persisted now — the mapping is written on form submit, scoped
    // to this outward (inwardId), so it can't leak into another Add flow.
    this.setState((s) => {
      const columns = {}
      ALL_KEYS.forEach((k) => {
        columns[k] = s.columns[k].slice()
      })
      const idx = columns[from].findIndex((i) => String(i._id) === String(dragged.id))
      if (idx !== -1) columns[from].splice(idx, 1)
      const cur = (s.poMedia[key] && s.poMedia[key].items) || []
      const poMedia = { ...s.poMedia, [key]: { items: [img, ...cur] } }
      // dropping an image also links this production order to the dispatch
      const linkedPOs = s.linkedPOs.indexOf(key) === -1 ? s.linkedPOs.concat(key) : s.linkedPOs
      return { columns, poMedia, linkedPOs, dragged: null, overPO: null, overCol: null, overIndex: null }
    })
    // linking via a drop also auto-fills the PO's own uploaded images
    this.ensurePOMedia([key])
  }

  // remove an image from a PO box — returns it to the available pool (staged;
  // the un-map is written to the DB when the form is submitted)
  unmapImage = (order, media, e) => {
    if (e) e.stopPropagation()
    const key = String(order._id)
    const isProd =
      media.category === 'production_order' || media.category === 'manufacturing_order'
    this.setState((s) => {
      const cur = (s.poMedia[key] && s.poMedia[key].items) || []
      const nextItems = cur.filter((m) => String(m._id) !== String(media._id))
      const general = (s.columns.general || []).slice()
      // an outward image returns to the Available pool; a PO's own production
      // image simply leaves the box (it isn't an available outward image and
      // must not be draggable into other categories — its outward link is
      // dropped on save, its productionOrderFormId preserved)
      if (!isProd && !general.some((g) => String(g._id) === String(media._id))) {
        general.push(media)
      }
      return {
        poMedia: { ...s.poMedia, [key]: { items: nextItems } },
        columns: { ...s.columns, general },
        // removing the last image also unlinks the order from this outward
        linkedPOs: nextItems.length ? s.linkedPOs : s.linkedPOs.filter((k) => k !== key),
      }
    })
  }

  // ---- Dispatch (Order List) box handlers — parallel to the PO ones ----
  toggleLinkDispatch = (order, e) => {
    if (e) e.stopPropagation()
    const id = String(order._id)
    const isLinking = this.state.linkedDispatchOrders.indexOf(id) === -1
    // one category at a time — can't link Dispatch while another is active
    if (isLinking && !this.isDroppable(DISPATCH_KEY)) return
    if (isLinking) {
      this.setAddTarget('dispatch', order)
      return
    }
    this.setState((s) => ({
      linkedDispatchOrders: s.linkedDispatchOrders.filter((k) => k !== id),
      addTarget: s.addTarget && s.addTarget.id === id ? null : s.addTarget,
    }))
  }

  onDispatchBoxDrop = (e, order) => {
    e.preventDefault()
    e.stopPropagation()
    const { dragged } = this.state
    if (!dragged) {
      this.setState({ overPO: null })
      return
    }
    if (!this.isDroppable(DISPATCH_KEY)) {
      // another category is active — one category at a time
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const from = dragged.from
    const srcArr = this.state.columns[from] || []
    const img = srcArr.find((i) => String(i._id) === String(dragged.id))
    if (!img) {
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const key = String(order._id)
    this.setState((s) => {
      const columns = {}
      ALL_KEYS.forEach((k) => {
        columns[k] = s.columns[k].slice()
      })
      const idx = columns[from].findIndex((i) => String(i._id) === String(dragged.id))
      if (idx !== -1) columns[from].splice(idx, 1)
      const cur = (s.dispatchMedia[key] && s.dispatchMedia[key].items) || []
      const dispatchMedia = { ...s.dispatchMedia, [key]: { items: [img, ...cur] } }
      const linkedDispatchOrders =
        s.linkedDispatchOrders.indexOf(key) === -1
          ? s.linkedDispatchOrders.concat(key)
          : s.linkedDispatchOrders
      return {
        columns,
        dispatchMedia,
        linkedDispatchOrders,
        dragged: null,
        overPO: null,
        overCol: null,
        overIndex: null,
      }
    })
    // linking via a drop also auto-fills the order's own uploaded images
    this.ensureOrderDispatchMedia([key])
  }

  unmapDispatchImage = (order, media, e) => {
    if (e) e.stopPropagation()
    const key = String(order._id)
    // an order's OWN uploaded image (already tagged with orderDispatchId) just
    // leaves the box; an outward image dragged from the pool returns to it
    const isOwn = !!media.orderDispatchId
    this.setState((s) => {
      const cur = (s.dispatchMedia[key] && s.dispatchMedia[key].items) || []
      const nextItems = cur.filter((m) => String(m._id) !== String(media._id))
      const general = (s.columns.general || []).slice()
      if (!isOwn && !general.some((g) => String(g._id) === String(media._id))) {
        general.push(media)
      }
      return {
        dispatchMedia: { ...s.dispatchMedia, [key]: { items: nextItems } },
        columns: { ...s.columns, general },
        // removing the last image also unlinks the order from this outward
        linkedDispatchOrders: nextItems.length
          ? s.linkedDispatchOrders
          : s.linkedDispatchOrders.filter((k) => k !== key),
      }
    })
  }

  // ---- Repair box handlers — parallel to the Dispatch ones ----
  toggleLinkRepair = (order, e) => {
    if (e) e.stopPropagation()
    const id = String(order._id)
    const isLinking = this.state.linkedRepairOrders.indexOf(id) === -1
    if (isLinking && !this.isDroppable(REPAIR_KEY)) return
    if (isLinking) {
      this.setAddTarget('repair', order)
      return
    }
    this.setState((s) => ({
      linkedRepairOrders: s.linkedRepairOrders.filter((k) => k !== id),
      addTarget: s.addTarget && s.addTarget.id === id ? null : s.addTarget,
    }))
  }

  onRepairBoxDrop = (e, order) => {
    e.preventDefault()
    e.stopPropagation()
    const { dragged } = this.state
    if (!dragged) {
      this.setState({ overPO: null })
      return
    }
    if (!this.isDroppable(REPAIR_KEY)) {
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const from = dragged.from
    const srcArr = this.state.columns[from] || []
    const img = srcArr.find((i) => String(i._id) === String(dragged.id))
    if (!img) {
      this.setState({ dragged: null, overPO: null, overCol: null, overIndex: null })
      return
    }
    const key = String(order._id)
    this.setState((s) => {
      const columns = {}
      ALL_KEYS.forEach((k) => {
        columns[k] = s.columns[k].slice()
      })
      const idx = columns[from].findIndex((i) => String(i._id) === String(dragged.id))
      if (idx !== -1) columns[from].splice(idx, 1)
      const cur = (s.repairMedia[key] && s.repairMedia[key].items) || []
      const repairMedia = { ...s.repairMedia, [key]: { items: [img, ...cur] } }
      const linkedRepairOrders =
        s.linkedRepairOrders.indexOf(key) === -1
          ? s.linkedRepairOrders.concat(key)
          : s.linkedRepairOrders
      return {
        columns,
        repairMedia,
        linkedRepairOrders,
        dragged: null,
        overPO: null,
        overCol: null,
        overIndex: null,
      }
    })
    // linking via a drop also auto-fills the order's own uploaded images
    this.ensureOrderDispatchMedia([key])
  }

  unmapRepairImage = (order, media, e) => {
    if (e) e.stopPropagation()
    const key = String(order._id)
    // an order's OWN uploaded image (already tagged with orderDispatchId) just
    // leaves the box; an outward image dragged from the pool returns to it
    const isOwn = !!media.orderDispatchId
    this.setState((s) => {
      const cur = (s.repairMedia[key] && s.repairMedia[key].items) || []
      const nextItems = cur.filter((m) => String(m._id) !== String(media._id))
      const general = (s.columns.general || []).slice()
      if (!isOwn && !general.some((g) => String(g._id) === String(media._id))) {
        general.push(media)
      }
      return {
        repairMedia: { ...s.repairMedia, [key]: { items: nextItems } },
        columns: { ...s.columns, general },
        // removing the last image also unlinks the order from this outward
        linkedRepairOrders: nextItems.length
          ? s.linkedRepairOrders
          : s.linkedRepairOrders.filter((k) => k !== key),
      }
    })
  }

  // ---- click-to-add (select an order, then click Available images to add) ----
  // map a kind to its column key / media state key / link state key
  kindMeta = (kind) =>
    kind === 'production'
      ? { col: MANUFACTURING_KEY, media: 'poMedia', link: 'linkedPOs' }
      : kind === 'dispatch'
      ? { col: DISPATCH_KEY, media: 'dispatchMedia', link: 'linkedDispatchOrders' }
      : { col: REPAIR_KEY, media: 'repairMedia', link: 'linkedRepairOrders' }

  orderLabel = (kind, order) =>
    kind === 'production'
      ? 'PO ' + order.productionOrderId
      : order.leadName || (order.id ? 'Order #' + order.id : 'Order')

  // mark an order as the active target for click-to-add (also links it)
  setAddTarget = (kind, order) => {
    const meta = this.kindMeta(kind)
    if (!this.isDroppable(meta.col)) return // another category is active
    const id = String(order._id)
    const isNewLink = this.state[meta.link].indexOf(id) === -1
    this.setState((s) => ({
      addTarget: { kind, id, label: this.orderLabel(kind, order) },
      [meta.link]: s[meta.link].indexOf(id) === -1 ? s[meta.link].concat(id) : s[meta.link],
    }))
    // selecting/linking an order auto-fills its own uploaded images
    if (kind === 'production') this.ensurePOMedia([id])
    else this.ensureOrderDispatchMedia([id])
    // let the parent auto-fill the outward form from a newly-linked Dispatch/
    // Repair order (Production orders don't carry the customer/shipping details)
    if (isNewLink && (kind === 'dispatch' || kind === 'repair') && this.props.onOrderLinked) {
      this.props.onOrderLinked(order, kind)
    }
  }

  clearAddTarget = () => this.setState({ addTarget: null })

  // add an Available-pool image to the active target's box (click-to-add)
  addImageToTarget = (item) => {
    const t = this.state.addTarget
    if (!t) return
    const meta = this.kindMeta(t.kind)
    const key = t.id
    this.setState((s) => {
      const cur = (s[meta.media][key] && s[meta.media][key].items) || []
      if (cur.some((m) => String(m._id) === String(item._id))) return null // already added
      const general = (s.columns.general || []).filter((g) => String(g._id) !== String(item._id))
      return {
        columns: { ...s.columns, general },
        [meta.media]: { ...s[meta.media], [key]: { items: [item, ...cur] } },
        [meta.link]: s[meta.link].indexOf(key) === -1 ? s[meta.link].concat(key) : s[meta.link],
      }
    })
  }

  // production order details shown on hover (Popover content)
  renderPODetails = (order) => {
    const qty =
      order.quantity != null && order.quantity !== ''
        ? `${order.quantity}${order.unitOfMeasurement ? ' ' + order.unitOfMeasurement : ''}`
        : ''
    const details = [
      ['Product', order.product],
      ['Quantity', qty],
      ['Priority', order.priority],
      ['Production Team', order.productionTeam],
      ['Created', order.createdDate ? moment(order.createdDate).format('DD-MM-YYYY') : ''],
      [
        'Expected Completion',
        order.expectedCompletionDate
          ? moment(order.expectedCompletionDate).format('DD-MM-YYYY')
          : '',
      ],
      ['Remarks', order.remarks],
    ].filter((d) => d[1] != null && d[1] !== '')
    return (
      <div style={{ maxWidth: 260 }}>
        {details.map((d) => (
          <div key={d[0]} style={{ fontSize: 12, marginBottom: 3 }}>
            <span style={{ color: '#999' }}>{d[0]}: </span>
            {d[1]}
          </div>
        ))}
      </div>
    )
  }

  renderPOBox = (o) => {
    const key = String(o._id)
    const media = this.state.poMedia[key]
    const items = (media && media.items) || []
    const isOver = this.state.overPO === key
    const linked = this.state.linkedPOs.indexOf(key) !== -1
    const isTarget = this.state.addTarget && this.state.addTarget.id === key
    return (
      <Popover
        key={key}
        placement="top"
        mouseEnterDelay={0.3}
        title={
          <span style={{ fontSize: 12 }}>
            PO {o.productionOrderId}
            {o.product ? ' · ' + o.product : ''}
          </span>
        }
        content={this.renderPODetails(o)}>
        <div
          onDragOver={(e) => this.onPOBoxDragOver(e, o)}
          onDrop={(e) => this.onPOBoxDrop(e, o)}
          onClick={() => this.setAddTarget('production', o)}
          style={{
            flex: '1 1 150px',
            minWidth: 150,
            maxWidth: 220,
            cursor: 'pointer',
            border: isOver || isTarget
              ? '2px solid #1890ff'
              : linked
              ? '1px solid #52c41a'
              : '1px solid #e8e8e8',
            borderRadius: 8,
            background: isOver || isTarget ? '#e6f7ff' : linked ? '#f6ffed' : '#fafafa',
            boxShadow: isTarget ? '0 0 0 3px rgba(24,144,255,0.15)' : 'none',
            padding: 8,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <b style={{ fontSize: 12 }}>PO {o.productionOrderId}</b>
            {o.priority ? (
              <Tag
                color={o.priority === 'High' ? 'red' : 'blue'}
                style={{ marginLeft: 6, transform: 'scale(0.85)', transformOrigin: 'left center' }}>
                {o.priority}
              </Tag>
            ) : null}
            <Tag style={{ marginLeft: 'auto', borderRadius: 10 }}>{items.length}</Tag>
            {items.length ? (
              <Icon
                type="eye"
                title="Preview images"
                onClick={(e) => {
                  e.stopPropagation()
                  this.openPreview(items, 0)
                }}
                style={{ marginLeft: 6, color: '#1890ff', fontSize: 14, cursor: 'pointer' }}
              />
            ) : null}
          </div>
          {/* link this production order to the outward dispatch */}
          <div
            onClick={(e) => this.toggleLinkPO(o, e)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 11,
              marginBottom: 6,
              padding: '1px 8px',
              borderRadius: 10,
              border: '1px solid ' + (linked ? '#52c41a' : '#d9d9d9'),
              color: linked ? '#52c41a' : '#888',
              background: linked ? '#f6ffed' : '#fff',
            }}>
            <Icon type={linked ? 'check' : 'plus'} style={{ fontSize: 10, marginRight: 4 }} />
            {linked ? 'Linked to this outward' : 'Link to this outward'}
          </div>
          {o.product ? (
            <div
              style={{
                fontSize: 11,
                color: '#888',
                marginBottom: 6,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {o.product}
            </div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 44 }}>
            {media && media.loading ? (
              <Spin size="small" />
            ) : items.length ? (
              items.map((m, mi) => (
                <div key={m._id} style={{ position: 'relative' }}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      this.openPreview(items, mi)
                    }}
                    title={m.remarks || ''}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid #e8e8e8',
                      background: '#000',
                      cursor: 'pointer',
                    }}>
                    {m.type === 'video' ? (
                      <video src={fullUrl(m.url)} style={mediaStyle} muted preload="metadata" />
                    ) : (
                      <img src={fullUrl(m.url)} style={mediaStyle} alt="" loading="lazy" />
                    )}
                  </div>
                  {/* remove only makes sense once the order is linked (mapping);
                      unlinked boxes show images for reference/preview only */}
                  {linked ? (
                    <Icon
                      type="close-circle"
                      theme="filled"
                      onClick={(e) => this.unmapImage(o, m, e)}
                      title="Remove"
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        color: '#ff4d4f',
                        background: '#fff',
                        borderRadius: '50%',
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    />
                  ) : null}
                </div>
              ))
            ) : (
              <div
                style={{ color: '#bbb', fontSize: 11, textAlign: 'center', width: '100%', padding: '12px 0' }}>
                Drop image here
              </div>
            )}
          </div>
        </div>
      </Popover>
    )
  }

  // ---- shared order-box section (search + capped render for performance) ----
  matchProductionOrder = (o, q) =>
    [o.productionOrderId, o.product, o.productionTeam, o.priority]
      .filter((v) => v != null && v !== '')
      .some((v) => String(v).toLowerCase().indexOf(q) !== -1)

  matchOrderDispatch = (o, q) => {
    const products = (o.products || []).map((p) => p && p.productName).filter(Boolean).join(' ')
    return [o.leadName, o.contactName, o.mobile, o.id, products]
      .filter((v) => v != null && v !== '')
      .some((v) => String(v).toLowerCase().indexOf(q) !== -1)
  }

  renderOrderSection = (cfg) => {
    const { orders, loading, search, setSearch, icon, color, title, emptyText, matchFn, renderBox } = cfg
    const q = (search || '').trim().toLowerCase()
    const filtered = q ? orders.filter((o) => matchFn(o, q)) : orders
    const shown = filtered.slice(0, ORDER_BOX_CAP)
    return (
      <div style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 8px', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <Icon type={icon} style={{ color, fontSize: 12, marginRight: 6 }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>{title}</span>
          <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>
            drop images, or click an order then click Available images to add
          </span>
          <Tag style={{ marginLeft: 'auto', borderRadius: 10 }}>{orders.length}</Tag>
        </div>
        <Input
          size="small"
          allowClear
          placeholder="Search orders"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 6 }}
        />
        {loading ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <Spin size="small" />
          </div>
        ) : !orders.length ? (
          <div style={{ color: '#bbb', textAlign: 'center', padding: '8px 0', fontSize: 12 }}>{emptyText}</div>
        ) : !shown.length ? (
          <div style={{ color: '#bbb', textAlign: 'center', padding: '8px 0', fontSize: 12 }}>No matches</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
            {shown.map((o) => renderBox(o))}
            {filtered.length > ORDER_BOX_CAP ? (
              <div style={{ width: '100%', textAlign: 'center', color: '#aaa', fontSize: 11, padding: '4px 0' }}>
                Showing {ORDER_BOX_CAP} of {filtered.length} — search to narrow down
              </div>
            ) : null}
          </div>
        )}
      </div>
    )
  }

  renderProductionBoxes = () =>
    this.renderOrderSection({
      orders: this.state.productionOrders,
      loading: this.state.poLoading,
      search: this.state.poSearch,
      setSearch: (v) =>
        this.setState({ poSearch: v }, () =>
          this.ensurePOMedia(
            this.visibleIds(this.state.productionOrders, v, this.matchProductionOrder)
          )
        ),
      icon: 'apartment',
      color: '#1890ff',
      title: 'Production Orders',
      emptyText: 'No production orders',
      matchFn: this.matchProductionOrder,
      renderBox: this.renderPOBox,
    })

  // ---- Dispatch (Order List) renderers — parallel to the PO ones ----
  renderDispatchDetails = (order) => {
    const products = (order.products || []).map((p) => p.productName).filter(Boolean).join(', ')
    const details = [
      ['Lead', order.leadName],
      ['Contact', order.contactName],
      ['Mobile', order.mobile],
      ['Products', products],
      ['Status', order.status],
      ['Order Date', order.orderDate ? moment(order.orderDate).format('DD-MM-YYYY') : ''],
      ['Est. Date', order.estimatedDate ? moment(order.estimatedDate).format('DD-MM-YYYY') : ''],
      ['Ship To', order.shipTo],
    ].filter((d) => d[1] != null && d[1] !== '')
    return (
      <div style={{ maxWidth: 260 }}>
        {details.map((d) => (
          <div key={d[0]} style={{ fontSize: 12, marginBottom: 3 }}>
            <span style={{ color: '#999' }}>{d[0]}: </span>
            {d[1]}
          </div>
        ))}
      </div>
    )
  }

  renderDispatchBox = (o) => {
    const key = String(o._id)
    const media = this.state.dispatchMedia[key]
    const items = (media && media.items) || []
    const isOver = this.state.overPO === key
    const linked = this.state.linkedDispatchOrders.indexOf(key) !== -1
    const isTarget = this.state.addTarget && this.state.addTarget.id === key
    const label = o.leadName || (o.id ? 'Order #' + o.id : 'Order')
    return (
      <Popover
        key={key}
        placement="top"
        mouseEnterDelay={0.3}
        title={
          <span style={{ fontSize: 12 }}>
            {label}
            {o.id ? ' · #' + o.id : ''}
          </span>
        }
        content={this.renderDispatchDetails(o)}>
        <div
          onDragOver={(e) => this.onPOBoxDragOver(e, o)}
          onDrop={(e) => this.onDispatchBoxDrop(e, o)}
          onClick={() => this.setAddTarget('dispatch', o)}
          style={{
            flex: '1 1 150px',
            minWidth: 150,
            maxWidth: 220,
            cursor: 'pointer',
            border: isOver || isTarget
              ? '2px solid #1890ff'
              : linked
              ? '1px solid #52c41a'
              : '1px solid #e8e8e8',
            borderRadius: 8,
            background: isOver || isTarget ? '#e6f7ff' : linked ? '#f6ffed' : '#fafafa',
            boxShadow: isTarget ? '0 0 0 3px rgba(24,144,255,0.15)' : 'none',
            padding: 8,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <b
              style={{
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 130,
              }}>
              {label}
            </b>
            <Tag style={{ marginLeft: 'auto', borderRadius: 10 }}>{items.length}</Tag>
            {items.length ? (
              <Icon
                type="eye"
                title="Preview images"
                onClick={(e) => {
                  e.stopPropagation()
                  this.openPreview(items, 0)
                }}
                style={{ marginLeft: 6, color: '#1890ff', fontSize: 14, cursor: 'pointer' }}
              />
            ) : null}
          </div>
          {/* link this order to the outward dispatch */}
          <div
            onClick={(e) => this.toggleLinkDispatch(o, e)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 11,
              marginBottom: 6,
              padding: '1px 8px',
              borderRadius: 10,
              border: '1px solid ' + (linked ? '#52c41a' : '#d9d9d9'),
              color: linked ? '#52c41a' : '#888',
              background: linked ? '#f6ffed' : '#fff',
            }}>
            <Icon type={linked ? 'check' : 'plus'} style={{ fontSize: 10, marginRight: 4 }} />
            {linked ? 'Linked to this outward' : 'Link to this outward'}
          </div>
          {o.contactName ? (
            <div
              style={{
                fontSize: 11,
                color: '#888',
                marginBottom: 6,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {o.contactName}
            </div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 44 }}>
            {items.length ? (
              items.map((m, mi) => (
                <div key={m._id} style={{ position: 'relative' }}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      this.openPreview(items, mi)
                    }}
                    title={m.remarks || ''}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid #e8e8e8',
                      background: '#000',
                      cursor: 'pointer',
                    }}>
                    {m.type === 'video' ? (
                      <video src={fullUrl(m.url)} style={mediaStyle} muted preload="metadata" />
                    ) : (
                      <img src={fullUrl(m.url)} style={mediaStyle} alt="" loading="lazy" />
                    )}
                  </div>
                  {linked ? (
                  <Icon
                    type="close-circle"
                    theme="filled"
                    onClick={(e) => this.unmapDispatchImage(o, m, e)}
                    title="Remove"
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      color: '#ff4d4f',
                      background: '#fff',
                      borderRadius: '50%',
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  />
                  ) : null}
                </div>
              ))
            ) : (
              <div
                style={{ color: '#bbb', fontSize: 11, textAlign: 'center', width: '100%', padding: '12px 0' }}>
                Drop image here
              </div>
            )}
          </div>
        </div>
      </Popover>
    )
  }

  renderDispatchBoxes = () =>
    this.renderOrderSection({
      orders: this.state.dispatchOrders,
      loading: this.state.dispatchLoading,
      search: this.state.dispatchSearch,
      setSearch: (v) =>
        this.setState({ dispatchSearch: v }, () =>
          this.ensureOrderDispatchMedia(
            this.visibleIds(this.state.dispatchOrders, v, this.matchOrderDispatch)
          )
        ),
      icon: 'profile',
      color: '#722ed1',
      title: 'Order List',
      emptyText: 'No finished-goods orders',
      matchFn: this.matchOrderDispatch,
      renderBox: this.renderDispatchBox,
    })

  // ---- Repair renderers — parallel to Dispatch (reuses renderDispatchDetails) ----
  renderRepairBox = (o) => {
    const key = String(o._id)
    const media = this.state.repairMedia[key]
    const items = (media && media.items) || []
    const isOver = this.state.overPO === key
    const linked = this.state.linkedRepairOrders.indexOf(key) !== -1
    const isTarget = this.state.addTarget && this.state.addTarget.id === key
    const label = o.leadName || (o.id ? 'Order #' + o.id : 'Order')
    return (
      <Popover
        key={key}
        placement="top"
        mouseEnterDelay={0.3}
        title={
          <span style={{ fontSize: 12 }}>
            {label}
            {o.id ? ' · #' + o.id : ''}
          </span>
        }
        content={this.renderDispatchDetails(o)}>
        <div
          onDragOver={(e) => this.onPOBoxDragOver(e, o)}
          onDrop={(e) => this.onRepairBoxDrop(e, o)}
          onClick={() => this.setAddTarget('repair', o)}
          style={{
            flex: '1 1 150px',
            minWidth: 150,
            maxWidth: 220,
            cursor: 'pointer',
            border: isOver || isTarget
              ? '2px solid #1890ff'
              : linked
              ? '1px solid #52c41a'
              : '1px solid #e8e8e8',
            borderRadius: 8,
            background: isOver || isTarget ? '#e6f7ff' : linked ? '#f6ffed' : '#fafafa',
            boxShadow: isTarget ? '0 0 0 3px rgba(24,144,255,0.15)' : 'none',
            padding: 8,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <b
              style={{
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 130,
              }}>
              {label}
            </b>
            <Tag style={{ marginLeft: 'auto', borderRadius: 10 }}>{items.length}</Tag>
            {items.length ? (
              <Icon
                type="eye"
                title="Preview images"
                onClick={(e) => {
                  e.stopPropagation()
                  this.openPreview(items, 0)
                }}
                style={{ marginLeft: 6, color: '#1890ff', fontSize: 14, cursor: 'pointer' }}
              />
            ) : null}
          </div>
          {/* link this repair order to the outward dispatch */}
          <div
            onClick={(e) => this.toggleLinkRepair(o, e)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 11,
              marginBottom: 6,
              padding: '1px 8px',
              borderRadius: 10,
              border: '1px solid ' + (linked ? '#52c41a' : '#d9d9d9'),
              color: linked ? '#52c41a' : '#888',
              background: linked ? '#f6ffed' : '#fff',
            }}>
            <Icon type={linked ? 'check' : 'plus'} style={{ fontSize: 10, marginRight: 4 }} />
            {linked ? 'Linked to this outward' : 'Link to this outward'}
          </div>
          {o.contactName ? (
            <div
              style={{
                fontSize: 11,
                color: '#888',
                marginBottom: 6,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {o.contactName}
            </div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 44 }}>
            {items.length ? (
              items.map((m, mi) => (
                <div key={m._id} style={{ position: 'relative' }}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      this.openPreview(items, mi)
                    }}
                    title={m.remarks || ''}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid #e8e8e8',
                      background: '#000',
                      cursor: 'pointer',
                    }}>
                    {m.type === 'video' ? (
                      <video src={fullUrl(m.url)} style={mediaStyle} muted preload="metadata" />
                    ) : (
                      <img src={fullUrl(m.url)} style={mediaStyle} alt="" loading="lazy" />
                    )}
                  </div>
                  {linked ? (
                  <Icon
                    type="close-circle"
                    theme="filled"
                    onClick={(e) => this.unmapRepairImage(o, m, e)}
                    title="Remove"
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      color: '#ff4d4f',
                      background: '#fff',
                      borderRadius: '50%',
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  />
                  ) : null}
                </div>
              ))
            ) : (
              <div
                style={{ color: '#bbb', fontSize: 11, textAlign: 'center', width: '100%', padding: '12px 0' }}>
                Drop image here
              </div>
            )}
          </div>
        </div>
      </Popover>
    )
  }

  renderRepairBoxes = () =>
    this.renderOrderSection({
      orders: this.state.repairOrders,
      loading: this.state.repairLoading,
      search: this.state.repairSearch,
      setSearch: (v) =>
        this.setState({ repairSearch: v }, () =>
          this.ensureOrderDispatchMedia(
            this.visibleIds(this.state.repairOrders, v, this.matchOrderDispatch)
          )
        ),
      icon: 'tool',
      color: '#fa8c16',
      title: 'Repair Orders',
      emptyText: 'No repaired-goods orders',
      matchFn: this.matchOrderDispatch,
      renderBox: this.renderRepairBox,
    })

  // ---- drag & drop (native HTML5, staged) ----
  onDragStart = (e, item, from) => {
    try {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(item._id))
    } catch (err) {
      /* noop */
    }
    this.setState({ dragged: { id: item._id, from } })
  }

  onDragEnd = () => this.setState({ dragged: null, overCol: null, overIndex: null, overPO: null })

  // The single category (of the 5) that currently holds images. While one is
  // "active", the other four are locked — images may only go into one category
  // at a time. Clearing it (removing all its images) unlocks all again.
  activeCategory = () => {
    const cols = this.state.columns
    const found = COLUMNS.find((c) => {
      if ((cols[c.key] || []).length > 0) return true
      // Manufacturing / Dispatch also count as "in use" once an order is linked
      // (via the link toggle, or by dropping an image on a box) — so the other
      // categories lock just like dropping into a normal category.
      if (c.key === MANUFACTURING_KEY && this.state.linkedPOs.length > 0) return true
      if (c.key === DISPATCH_KEY && this.state.linkedDispatchOrders.length > 0) return true
      if (c.key === REPAIR_KEY && this.state.linkedRepairOrders.length > 0) return true
      return false
    })
    return found ? found.key : null
  }

  // pool is always droppable; a category accepts drops only when there is no
  // active category yet, or it IS the active category
  isDroppable = (col) => {
    if (col === 'general') return true
    const active = this.activeCategory()
    return !active || active === col
  }

  onCardDragOver = (e, col, index) => {
    if (!this.state.dragged) return
    if (!this.isDroppable(col)) return // locked category — don't allow the drop
    e.preventDefault()
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const after =
      col === 'general'
        ? e.clientX - rect.left > rect.width / 2
        : e.clientY - rect.top > rect.height / 2
    const target = after ? index + 1 : index
    if (this.state.overCol !== col || this.state.overIndex !== target) {
      this.setState({ overCol: col, overIndex: target })
    }
  }

  onColumnDragOver = (e, col) => {
    if (!this.state.dragged) return
    if (!this.isDroppable(col)) return // locked category
    e.preventDefault()
    const end = this.state.columns[col].length
    if (this.state.overCol !== col || this.state.overIndex !== end) {
      this.setState({ overCol: col, overIndex: end })
    }
  }

  onColumnDrop = (e, col) => {
    e.preventDefault()
    const { dragged, overCol, overIndex } = this.state
    if (!dragged) return
    const destCol = overCol != null ? overCol : col
    if (!this.isDroppable(destCol)) {
      // locked category — cancel the drop, leaving the image where it was
      this.setState({ dragged: null, overCol: null, overIndex: null })
      return
    }
    this.setState((state) => {
      const columns = {}
      ALL_KEYS.forEach((k) => {
        columns[k] = state.columns[k].slice()
      })
      const from = dragged.from
      const srcArr = columns[from]
      const idx = srcArr.findIndex((i) => String(i._id) === String(dragged.id))
      if (idx === -1) return { dragged: null, overCol: null, overIndex: null }
      const [item] = srcArr.splice(idx, 1)
      const destArr = columns[destCol]
      let insertAt = overIndex == null ? destArr.length : overIndex
      if (from === destCol && idx < insertAt) insertAt -= 1
      if (insertAt < 0) insertAt = 0
      if (insertAt > destArr.length) insertAt = destArr.length
      destArr.splice(insertAt, 0, item)
      return { columns, dragged: null, overCol: null, overIndex: null }
    })
  }

  // open the shared MediaViewer lightbox on `list` at `index`. Set the list
  // FIRST, then the open index on the next tick: the controlled MediaViewer reads
  // items.length when openIndex changes, so if both change together it sees a
  // stale (empty) length and immediately closes.
  openPreview = (list, index) => {
    const idx = typeof index === 'number' ? index : 0
    this.setState({ previewItems: list || [] }, () => {
      this.setState({ previewIndex: idx })
    })
  }
  closePreview = () => this.setState({ previewIndex: -1 })

  // full details panel shown inside the MediaViewer lightbox
  renderImageDetails = (current) => {
    const m = (current && current.raw) || {}
    const rows = [
      ['Order', m.orderId ? '#' + m.orderId : ''],
      ['Uploaded By', m.userName],
      ['Captured', m.capturedAt ? moment(m.capturedAt).format('DD-MM-YYYY HH:mm') : ''],
      ['Status', m.status],
      ['Category', m.subCategory],
      ['Remarks', m.remarks],
    ].filter((r) => r[1])
    if (!rows.length) return null
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Image Details</div>
        <Descriptions size="small" column={1} bordered>
          {rows.map((r) => (
            <Descriptions.Item key={r[0]} label={r[0]}>
              {r[1]}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    )
  }

  // per-image remark typed in a category column (saved on submit)
  setRemark = (id, text) => {
    this.setState((s) => ({ remarks: { ...s.remarks, [id]: text } }))
  }

  renderCard = (item, col, index) => {
    const url = fullUrl(item.url)
    const isVideo = item.type === 'video'
    const dragging = this.state.dragged && String(this.state.dragged.id) === String(item._id)
    // in the Available pool, when an order is the active add-target, clicking a
    // thumbnail adds it to that order instead of opening the preview
    const adding = col === 'general' && !!this.state.addTarget
    const thumb = (
      <div
        style={{
          ...cardStyle,
          opacity: dragging ? 0.35 : 1,
          boxShadow: adding ? 'inset 0 0 0 2px #1890ff' : 'none',
        }}
        draggable
        onDragStart={(e) => this.onDragStart(e, item, col)}
        onDragEnd={this.onDragEnd}
        onDragOver={(e) => this.onCardDragOver(e, col, index)}
        onClick={() =>
          adding
            ? this.addImageToTarget(item)
            : this.openPreview(this.state.columns[col] || [], index)
        }>
        {isVideo ? (
          <video src={url} style={mediaStyle} muted preload="metadata" draggable={false} />
        ) : (
          <img src={url} style={mediaStyle} loading="lazy" alt="" draggable={false} />
        )}
        {adding ? (
          <div
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: '#1890ff',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
            }}>
            <Icon type="plus" />
          </div>
        ) : null}
      </div>
    )
    // the available pool shows just the thumbnail
    if (col === 'general') return React.cloneElement(thumb, { key: item._id })
    // inside a category — add a per-image Remarks field, saved with the outward
    const rm = this.state.remarks[item._id]
    return (
      <div key={item._id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {thumb}
        <Input
          size="small"
          placeholder="Remarks"
          value={rm != null ? rm : item.remarks || ''}
          onChange={(e) => this.setRemark(item._id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ width: 130, fontSize: 11 }}
        />
      </div>
    )
  }

  renderCards = (col, horizontal) => {
    const items = this.state.columns[col] || []
    const { overCol, overIndex } = this.state
    const line = (k) => (
      <div
        key={k}
        style={
          horizontal
            ? { width: 3, alignSelf: 'stretch', background: '#1890ff', borderRadius: 2 }
            : { height: 3, width: '100%', background: '#1890ff', borderRadius: 2 }
        }
      />
    )
    const nodes = []
    items.forEach((item, index) => {
      if (overCol === col && overIndex === index) nodes.push(line('l-' + col + '-' + index))
      nodes.push(this.renderCard(item, col, index))
    })
    if (overCol === col && overIndex === items.length) nodes.push(line('l-' + col + '-end'))
    return nodes
  }

  renderColumn = (colMeta) => {
    const { key, title, accent } = colMeta
    const items = this.state.columns[key] || []
    const isOver = this.state.overCol === key
    const locked = !this.isDroppable(key) // another category is active
    const isManufacturing = key === MANUFACTURING_KEY
    const isDispatch = key === DISPATCH_KEY
    const isRepair = key === REPAIR_KEY
    // columns that carry an order-box section don't show the generic "Drop
    // here" image zone — it's redundant next to the boxes — and are wider
    const hasBoxes = isManufacturing || isDispatch || isRepair
    return (
      <div
        key={key}
        style={{
          flex: hasBoxes ? '2 1 300px' : '1 1 150px',
          minWidth: hasBoxes ? 260 : 150,
          background: locked ? '#f5f5f5' : '#fafafa',
          borderRadius: 8,
          border: isOver ? '2px dashed ' + accent : '2px solid #f0f0f0',
          opacity: locked ? 0.55 : 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        onDragOver={(e) => this.onColumnDragOver(e, key)}
        onDrop={(e) => this.onColumnDrop(e, key)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ width: 4, height: 14, borderRadius: 2, background: accent }} />
          <span style={{ fontWeight: 600, fontSize: 13 }}>{title}</span>
          {locked ? <Icon type="lock" style={{ color: '#bbb', fontSize: 12 }} /> : null}
          <Tag color={accent} style={{ marginLeft: 'auto', borderRadius: 10 }}>{items.length}</Tag>
        </div>
        {/* Manufacturing → Production Orders; Dispatch/Repair → Order List (drop-to-map) */}
        {isManufacturing ? this.renderProductionBoxes() : null}
        {isDispatch ? this.renderDispatchBoxes() : null}
        {isRepair ? this.renderRepairBoxes() : null}
        {items.length || isOver ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, minHeight: hasBoxes ? 0 : 74 }}>
            {this.renderCards(key, false)}
          </div>
        ) : hasBoxes ? null : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, minHeight: 74 }}>
            <div style={{ color: '#bbb', textAlign: 'center', padding: '18px 0', fontSize: 12 }}>
              {locked ? 'Locked' : 'Drop here'}
            </div>
          </div>
        )}
      </div>
    )
  }

  render() {
    const { loading, columns } = this.state
    const pool = columns.general || []
    const active = this.activeCategory()
    const activeMeta = active ? COLUMNS.find((c) => c.key === active) : null
    const activeTitle = activeMeta ? activeMeta.title : ''
    const activeAccent = activeMeta ? activeMeta.accent : '#1890ff'
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>Outward Images</span>
          <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
            Drag images into one category to attach them to this order — images may go into only
            one category at a time. Saved when you submit the form.
          </span>
          {active ? (
            <Tag color={activeAccent} style={{ marginLeft: 8, borderRadius: 10 }}>
              Category: {activeTitle}
            </Tag>
          ) : null}
        </div>
        <Spin spinning={loading}>
          {/* available pool */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              minHeight: 74,
              alignContent: 'flex-start',
              padding: 8,
              border: this.state.overCol === 'general' ? '2px dashed #1890ff' : '1px solid #f0f0f0',
              borderRadius: 8,
              marginBottom: 10,
              background: '#fff',
            }}
            onDragOver={(e) => this.onColumnDragOver(e, 'general')}
            onDrop={(e) => this.onColumnDrop(e, 'general')}>
            <div style={{ width: '100%', fontSize: 12, color: '#888', marginBottom: 2 }}>
              Available images <Tag>{pool.length}</Tag>
            </div>
            {/* click-to-add banner: an order is selected as the target */}
            {this.state.addTarget ? (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: 6,
                  padding: '6px 10px',
                  marginBottom: 8,
                }}>
                <Icon type="plus-circle" style={{ color: '#1890ff' }} />
                <span style={{ fontSize: 12 }}>
                  Adding to <b>{this.state.addTarget.label}</b> — click images below to add them.
                </span>
                <Button size="small" style={{ marginLeft: 'auto' }} onClick={this.clearAddTarget}>
                  Done
                </Button>
              </div>
            ) : null}
            {pool.length || this.state.overCol === 'general' ? (
              this.renderCards('general', true)
            ) : (
              <div style={{ color: '#bbb', width: '100%', textAlign: 'center', padding: '14px 0', fontSize: 12 }}>
                No available outward images
              </div>
            )}
          </div>

          {/* five category columns */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', overflowX: 'auto' }}>
            {COLUMNS.map(this.renderColumn)}
          </div>
        </Spin>

        {/* shared MediaViewer lightbox (navigation + details), controlled */}
        <MediaViewer
          lightboxOnly
          items={this.state.previewItems}
          openIndex={this.state.previewIndex}
          onClose={this.closePreview}
          detailsPanel={this.renderImageDetails}
        />
      </div>
    )
  }
}

export default OutwardImageBoard
